import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import AnswerStyleEditor from '@/components/answerStyle/AnswerStyleEditor';
import { sameAnswerStyle, type AnswerStyle } from '@/components/chat/AnswerStyle';
import { SaveStatus, type SaveState } from '@/components/settings/SaveStatus';
import { useAnswerStyle } from '@/hooks/useAnswerStyle';
import useAnswerStyleActions, { type AnswerStyleSaveResult } from '@/hooks/useAnswerStyleActions';
import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';
import { SettingsSection, useSettingsControlHeight, useSettingsSurface } from './SettingsSection';
import type { SettingsEditState } from './settingsTypes';

const SAVED_VISIBLE_MS = 2400;
const SAVED_KEY: Record<AnswerStyleSaveResult, string> = {
  account: 'answerStyle.note.accountSaved',
  browser: 'answerStyle.note.browserSaved',
  memory: 'answerStyle.note.visitOnly',
};

const FOCUS_VISIBLE = {
  '&:focus-visible': {
    outline: `2px solid ${semanticColors.brandPrimary}`,
    outlineOffset: '2px',
  },
} as const;

/**
 * Settings → Answer style. Same controls and example as the onboarding dialog;
 * edits stay a draft until "Save changes" succeeds, and "Cancel" restores the
 * saved choice. The desktop dialog reserves the action row so switching
 * choices does not move the example or sources below it. A successful save shapes
 * future answers only — nothing already in a conversation is rewritten.
 */
export default function AnswerStyleSettingsSection({
  onEditStateChange,
}: {
  onEditStateChange?: (state: SettingsEditState) => void;
} = {}) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const surface = useSettingsSurface();
  const controlHeight = useSettingsControlHeight();
  const snapshot = useAnswerStyle();
  const { save, signedIn } = useAnswerStyleActions();
  const [draft, setDraft] = useState<AnswerStyle>(snapshot.style);
  const [state, setState] = useState<SaveState>('idle');
  const [savedKey, setSavedKey] = useState(SAVED_KEY.account);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Follow the saved value while the voter is not mid-edit — a hydration or a
  // save from the composer lands here — without discarding an unsaved draft.
  const [lastSaved, setLastSaved] = useState(snapshot.style);
  if (!sameAnswerStyle(lastSaved, snapshot.style)) {
    setLastSaved(snapshot.style);
    if (sameAnswerStyle(draft, lastSaved)) setDraft(snapshot.style);
  }

  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    },
    []
  );

  const ready = snapshot.hydrated && (snapshot.scope === 'account') === signedIn;
  const dirty = !sameAnswerStyle(draft, snapshot.style);
  const saving = state === 'saving';
  const discard = useCallback(() => {
    setDraft(snapshot.style);
    setState('idle');
  }, [snapshot.style]);
  useEffect(() => {
    onEditStateChange?.({ dirty, saving, discard });
  }, [dirty, saving, discard, onEditStateChange]);

  // Return focus before the draft actions become unavailable.
  const headingRef = useRef<HTMLElement>(null);

  const handleSave = async () => {
    if (saving || !dirty) return;
    if (savedTimer.current) clearTimeout(savedTimer.current);
    headingRef.current?.focus({ preventScroll: surface === 'dialog' });
    setState('saving');
    try {
      const result = await save(draft, 'settings');
      setSavedKey(SAVED_KEY[result]);
      setState('saved');
      savedTimer.current = setTimeout(() => setState('idle'), SAVED_VISIBLE_MS);
    } catch {
      setState('error');
    }
  };

  let note: string;
  if (!signedIn && snapshot.guestStorage === 'memory') {
    note = tc('answerStyle.note.visitOnly');
  } else if (surface === 'dialog' || dirty || !snapshot.explicit) {
    note = signedIn ? tc('answerStyle.note.accountBefore') : tc('answerStyle.note.browserBefore');
  } else {
    note = signedIn ? tc('answerStyle.note.accountSaved') : tc('answerStyle.note.browserStored');
  }

  const feedbackStatus = (
    <SaveStatus
      state={state}
      error={tc('answerStyle.error')}
      savingLabel={tc('answerStyle.actions.saving')}
      savedLabel={tc(savedKey)}
    />
  );

  const draftActions = (
    <Box
      aria-hidden={!dirty}
      sx={{ display: 'flex', gap: '4px', visibility: dirty ? 'visible' : 'hidden' }}
    >
      <Button
        onClick={() => {
          headingRef.current?.focus({ preventScroll: surface === 'dialog' });
          discard();
        }}
        disabled={saving || !dirty}
        sx={{
          textTransform: 'none',
          fontSize: 13,
          color: semanticColors.textSecondary,
          minHeight: controlHeight,
          padding: '0 12px',
          ...FOCUS_VISIBLE,
        }}
      >
        {tc('answerStyle.actions.cancel')}
      </Button>
      <Button
        onClick={() => void handleSave()}
        disabled={saving || !dirty}
        aria-busy={saving}
        variant="contained"
        disableElevation
        sx={{
          position: 'relative',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600,
          height: controlHeight,
          minHeight: controlHeight,
          padding: '0 16px',
          borderRadius: BORDER_RADIUS,
          backgroundColor: semanticColors.textPrimary,
          color: semanticColors.textOnPrimary,
          '&:hover': { backgroundColor: semanticColors.hover },
          '&.Mui-disabled': {
            backgroundColor: semanticColors.disabledBackground,
            color: semanticColors.textSecondary,
          },
          ...FOCUS_VISIBLE,
        }}
      >
        {/* Spinner overlays the hidden label so saving neither shifts the width
            nor reserves an icon slot beside the text. */}
        <Box component="span" sx={{ opacity: saving ? 0 : 1 }}>
          {tc('answerStyle.actions.save')}
        </Box>
        {saving && (
          <CircularProgress
            size={14}
            aria-hidden="true"
            sx={{ position: 'absolute', color: 'inherit' }}
          />
        )}
      </Button>
    </Box>
  );

  const draftFeedback = (
    <Box>
      <Typography
        sx={{
          mt: surface === 'dialog' ? '12px' : '16px',
          fontSize: surface === 'dialog' ? 13 : 12,
          color: semanticColors.textTertiary,
        }}
      >
        {note}
      </Typography>
      <Typography
        sx={{
          mt: '4px',
          fontSize: surface === 'dialog' ? 13 : 12,
          color: semanticColors.textTertiary,
        }}
      >
        {tc('answerStyle.note.appliesNext')}
      </Typography>
      <Box sx={{ mt: '8px' }}>{feedbackStatus}</Box>
      {dirty && (
        <Box
          sx={{
            mt: '14px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {draftActions}
        </Box>
      )}
    </Box>
  );

  const dialogGuidance =
    !signedIn && snapshot.guestStorage === 'memory'
      ? `${tc('answerStyle.note.visitOnly')} ${tc('answerStyle.note.appliesNext')}`
      : t(signedIn ? 'answerStyleSyncNote' : 'answerStyleLocalNote');

  const dialogFeedback = (
    <Box sx={{ mt: '8px' }}>
      <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: semanticColors.textTertiary }}>
        {dialogGuidance}
      </Typography>
      <Box sx={{ mt: '4px', minHeight: 24 }}>{feedbackStatus}</Box>
    </Box>
  );

  return (
    <SettingsSection labelledBy="settings-answer-style-label">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', mb: '16px' }}>
        {surface === 'page' && (
          <Box sx={{ mt: '2px', flexShrink: 0, lineHeight: 0 }}>
            <ChatBubbleLeftRightIcon
              style={{ width: 20, height: 20, color: semanticColors.textPrimary }}
              aria-hidden="true"
            />
          </Box>
        )}
        <Box>
          <Typography
            id="settings-answer-style-label"
            ref={headingRef}
            tabIndex={-1}
            sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.3, outline: 'none' }}
          >
            {t('answerStyleTitle')}
          </Typography>
          <Typography
            sx={{
              fontSize: surface === 'dialog' ? '14px' : '13px',
              lineHeight: 1.5,
              color: semanticColors.textTertiary,
            }}
          >
            {t('answerStyleDesc')}
          </Typography>
        </Box>
      </Box>

      {!ready ? (
        <CircularProgress size={20} sx={{ color: semanticColors.textPrimary }} />
      ) : (
        <>
          <AnswerStyleEditor
            value={draft}
            onChange={(next) => {
              if (surface === 'dialog') {
                if (saving) return;
                if (savedTimer.current) clearTimeout(savedTimer.current);
                setState('idle');
              }
              setDraft(next);
            }}
            source="settings"
            comfortable={surface === 'dialog'}
            exampleToolbarActions={surface === 'dialog' ? draftActions : undefined}
            beforeExample={surface === 'dialog' ? dialogFeedback : undefined}
          />
          {surface === 'page' && draftFeedback}
        </>
      )}
    </SettingsSection>
  );
}
